import React, { useState, useEffect } from 'react';
import './ImagePicker.css';
import { getImages, uploadImage } from '../api/imageApi';
import { compressImageFile } from '../utils/imageCompression';

const ImagePicker = ({ isOpen, onClose, onSelect }) => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imagesPerPage] = useState(10);

  const onConfirm = () => {
    onSelect(selectedImage);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      loadImages();
    }
  }, [isOpen, currentPage]);

  const loadImages = async () => {
    try {
      setIsLoading(true);
      const response = await getImages(currentPage, imagesPerPage);
      setImages(response.data);
      setTotalPages(response.pagination.total_pages);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageClick = (url, id) => {
    setSelectedImage(url);
    setSelectedImageId(id);
  };

  const handleImageDoubleClick = (url) => {
    onSelect(url);
    onClose();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image size should be less than 10MB');
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);
      
      // Compress the image before upload - more aggressive compression
      const compressedDataUrl = await compressImageFile(file, {
        quality: 0.8,
        maxWidth: 1920,
        maxHeight: 1920,
        outputFormat: 'jpeg'
      });
      
      // Convert data URL back to File for upload
      const compressedFile = dataUrlToFile(compressedDataUrl, file.name);
      
      const response = await uploadImage(compressedFile, { type: 'imageType' });
      if (response && response.image_url) {
        loadImages(); // Reload images to get updated list with new image at the top
        setSelectedImage(response.image_url);
        setSelectedImageId(response.id);
        onSelect(response.image_url); // Call onSelect immediately after successful upload
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      let errorMessage = 'Failed to upload image. Please try again.';
      if (error.response?.status === 413) {
        errorMessage = 'Image is too large. Please select a smaller image.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      setUploadError(errorMessage);
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // Helper function to convert data URL back to File
  const dataUrlToFile = (dataUrl, filename) => {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename.replace(/\.[^/.]+$/, '.jpg'), { type: mime });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="image-picker-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="image-picker-content">
        <h2>Seleccionar imagen</h2>
        
        <div className="upload-section">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            disabled={isUploading}
            style={{ display: 'none' }}
            id="image-upload"
          />
          <label htmlFor="image-upload" className="upload-button">
            {isUploading ? 'Subiendo...' : 'Subir nueva imagen'}
          </label>
          {uploadError && <div className="error-message">{uploadError}</div>}
        </div>

        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <span>
            <div className="image-grid">
              {images.map((image, index) => (
                <div
                  key={index}
                  className={`image-item ${selectedImageId === image.id ? 'selected' : ''}`}
                  onClick={() => handleImageClick(image.image_url, image.id)}
                  onDoubleClick={() => handleImageDoubleClick(image.image_url)}
                >
                  <img src={image.image_url} alt="Selectable" />
                  <div className="tick">✔</div>
                </div>
              ))}
            </div>
            <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0' }}>
              <button 
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ 
                  padding: '8px 12px', 
                  margin: '0 5px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Anterior
              </button>
              <span style={{ margin: '0 15px', fontSize: '14px' }}>
                Página {currentPage} de {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                style={{ 
                  padding: '8px 12px', 
                  margin: '0 5px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  backgroundColor: (currentPage === totalPages || totalPages === 0) ? '#f5f5f5' : '#fff',
                  cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                Siguiente
              </button>
            </div>
            <div className="modal-buttons">
              <button className="button button-grey" onClick={onClose}>
                Cancelar
              </button>
              <button 
                className="button button-green" 
                onClick={(e) => {e.preventDefault(); onConfirm();}}
                disabled={!selectedImage}
              >
                Guardar
              </button>
            </div>
          </span>
        )}
      </div>
    </div>
  );
};

export default ImagePicker;
